/* eslint-disable react/prop-types */
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'baseui/snackbar';
import { Spinner } from 'baseui/spinner';
import { isEqual } from 'date-fns';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  ButtonContext,
  Dialog,
  DialogTrigger,
  Heading,
  Label,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Provider,
  Text,
  TextArea,
  TextAreaContext,
  TextContext,
  TextField,
  TextProps,
  useSlottedContext,
} from 'react-aria-components';
import {
  AlertTriangle,
  FileText,
  Plus as PlusIcon,
  X,
  User as UserIcon,
} from 'react-feather';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  get as getFieldError,
  useForm,
  useFormState,
} from 'react-hook-form';
import { z } from 'zod';

import { QueryResolver } from 'components/QueryResolver';
import { isMutationSuccess } from 'services/api/base-api';
import { ServiceLocationService } from 'services/api/service-locations/endpoints';
import {
  NoteType,
  ServiceLocationNote,
} from 'services/api/service-locations/types';
import { useTypedSelector } from 'store/store';
import { cn, formatDateTime } from 'utils/helpers';
import { useLocale } from 'utils/hooks/useLocale';

/**
 * https://github.com/react-hook-form/react-hook-form/issues/1005#issuecomment-988380981
 */
const stopPropagate = (callback: () => void) => {
  return (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };
};

const schema = z.object({
  note: z
    .string()
    .min(1, { message: 'errors.invalid_type_received_undefined' }),
});
type FormFields = z.infer<typeof schema>;

export default function ServiceLocationNotes({
  locationNote,
  readOnly = false,
}: {
  locationNote: {
    workRequestId: number;
    type: NoteType;
    serviceLocationId: number;
  };
  readOnly?: boolean;
}) {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const query = ServiceLocationService.endpoints.notes.useQuery(
    {
      params: {
        workRequestId: locationNote.workRequestId,
        noteType: locationNote.type,
      },
    },
    { refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  const userId = useTypedSelector((s) => s.auth.userCredentials.fsaUserId);
  const [createNote, mutation] =
    ServiceLocationService.endpoints.createNote.useMutation();
  const isLoading = mutation.isLoading;
  const snackbar = useSnackbar();

  return (
    <div className="font-sans">
      {!readOnly && (
        <nav className="flex justify-end">
          {query.data?.length !== 0 && (
            <Button
              type="button"
              className="inline-flex items-center rounded-md bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary-600"
              onPress={() => setIsOpen(true)}
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              {t('New Note')}
            </Button>
          )}
        </nav>
      )}
      <div className="mt-2">
        <QueryResolver query={query}>
          {(notes) => (
            <ListBox
              aria-label={t('Notes')}
              className="max-h-[20rem] divide-y divide-gray-100 overflow-y-auto"
              items={notes}
              selectionMode="none"
              renderEmptyState={(props) => (
                <div className="text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    {t('notes_empty')}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {/* Get started by creating a new note. */}
                    {t('notes_empty_description')}
                  </p>
                  {!readOnly && (
                    <div className="mt-6">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary-600"
                        onClick={() => setIsOpen(true)}
                      >
                        <PlusIcon
                          className="-ml-0.5 mr-1.5 h-5 w-5"
                          aria-hidden="true"
                        />
                        {t('New Note')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            >
              {(note) => (
                <ListBoxItem
                  textValue={note.createdByName}
                  className="flex gap-x-4 pb-4 first:pt-0 last:pb-4 [&:not(:first-child)]:pt-4"
                >
                  <div className="min-w-0">
                    <div className="flex-auto">
                      <div className="flex items-baseline justify-between gap-x-4">
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                          <p className="text-sm font-semibold leading-6 text-gray-900">
                            {note.createdByName}
                          </p>
                        </div>
                        <p className="flex-none text-xs text-gray-600">
                          {/* <time dateTime={comment.dateTime}>{comment.date}</time> */}
                        </p>
                      </div>
                      <ExpandableText>
                        {({ isClampable, isClamped }) => (
                          <>
                            <ExpandableParagraph className="mt-1 text-sm leading-6 text-gray-600">
                              {note.noteValue}
                            </ExpandableParagraph>
                            {isClamped && isClampable && (
                              <Button
                                slot="expander"
                                className="text-sm font-medium leading-6 text-brand-primary-600 hover:text-brand-primary-500"
                              >
                                {isClamped ? t('See More') : t('See Less')}
                              </Button>
                            )}
                          </>
                        )}
                      </ExpandableText>
                    </div>
                    <div className="mt-1 flex items-center space-x-2 text-xs leading-5 text-gray-500">
                      {!isEqual(
                        new Date(note.dateCreated),
                        new Date(note.dateModified),
                      ) && (
                        <>
                          <p className="whitespace-nowrap">{t('Edited')}</p>
                          <svg
                            viewBox="0 0 2 2"
                            className="h-0.5 w-0.5 fill-current"
                          >
                            <circle cx={1} cy={1} r={1} />
                          </svg>
                        </>
                      )}
                      <p className="whitespace-nowrap">
                        <time dateTime={note.dateCreated}>
                          {formatDateTime(new Date(note.dateCreated))}
                        </time>
                      </p>
                      {/* <svg
                        viewBox="0 0 2 2"
                        className="h-0.5 w-0.5 fill-current"
                      >
                        <circle cx={1} cy={1} r={1} />
                      </svg>
                      <p className="truncate">
                        Created by {note.createdByName}
                      </p> */}
                      {!readOnly && userId === note.createdBy && (
                        <nav className="flex items-center space-x-2">
                          <EditNoteDialog note={note} />
                          <svg
                            viewBox="0 0 2 2"
                            className="h-0.5 w-0.5 fill-current"
                          >
                            <circle cx={1} cy={1} r={1} />
                          </svg>
                          <DeleteNoteAlertDialog note={note} />
                        </nav>
                      )}
                    </div>
                  </div>
                </ListBoxItem>
              )}
            </ListBox>
          )}
        </QueryResolver>
      </div>

      {!readOnly && (
        <ModalOverlay
          className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/50 py-3 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0"
          onOpenChange={setIsOpen}
          isOpen={isOpen}
          isDismissable
        >
          <Modal className="relative z-50 grid w-full max-w-lg gap-4 rounded-2xl border bg-white p-6 shadow-lg duration-200 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 md:w-full">
            <Dialog className="outline-none">
              {(props) => (
                <>
                  <CloseButton {...props} />
                  <Heading
                    slot="title"
                    className="text-lg font-semibold leading-none tracking-tight"
                  >
                    {t('Create New Note')}
                  </Heading>
                  <div className="pt-4">
                    <NoteForm
                      id="create-note-form"
                      onSubmit={async (data) => {
                        const result = await createNote({
                          workRequestId: locationNote.workRequestId,
                          noteType: locationNote.type,
                          noteValue: data.note,
                        });
                        if (isMutationSuccess(result)) {
                          setIsOpen(false);
                          snackbar.enqueue({
                            // message: 'Note Added!'
                            message: t('note_create_success'),
                          });
                        } else {
                          snackbar.enqueue({
                            // message: 'Failed to add note. Try again.',
                            message: t('note_create_error'),
                          });
                        }
                      }}
                    />
                    <div className="mt-2 flex justify-end space-x-2">
                      <Button
                        type="button"
                        className={cn([
                          'mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto',
                          // Disabled
                          'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                        ])}
                        onPress={() => setIsOpen(false)}
                        isDisabled={isLoading}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        form="create-note-form"
                        className={cn([
                          'inline-flex items-center rounded-md bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary-600',
                          // Disabled
                          'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                        ])}
                        isDisabled={isLoading}
                      >
                        {isLoading && (
                          <Spinner
                            className="mr-2"
                            $size={16}
                            $borderWidth={2}
                          />
                        )}
                        {t('Add Note')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Dialog>
          </Modal>
        </ModalOverlay>
      )}
    </div>
  );
}

type ExpandableTextValue = {
  isClamped: boolean;
  isClampable: boolean;
};
const ExpandableTextContext = createContext<ExpandableTextValue>({
  isClamped: true,
  isClampable: false,
});
function ExpandableText({
  children,
}: {
  children: React.ReactNode | ((args: ExpandableTextValue) => React.ReactNode);
}) {
  const elRef = useRef<HTMLParagraphElement | null>(null);
  const [isClamped, setIsClamped] = useState(true);
  const [canBeClamped, setCanBeClamped] = useState(false);

  useEffect(() => {
    const handler = () => {
      if (elRef.current) {
        const { clientHeight, scrollHeight } = elRef.current;
        setCanBeClamped(clientHeight !== scrollHeight);
      }
    };
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  useEffect(() => {
    if (elRef.current) {
      const { clientHeight, scrollHeight } = elRef.current;
      setIsClamped(clientHeight !== scrollHeight);
      setCanBeClamped(clientHeight !== scrollHeight);
    }
  }, []);

  return (
    <Provider
      values={[
        [ExpandableTextContext, { isClamped, isClampable: canBeClamped }],
        [TextContext, { ref: elRef }],
        [
          ButtonContext,
          {
            slots: {
              expander: {
                onPress: () => setIsClamped((prev) => !prev),
              },
            },
          },
        ],
      ]}
    >
      {typeof children === 'function'
        ? children({ isClamped, isClampable: canBeClamped })
        : children}
    </Provider>
  );
}

function ExpandableParagraph(props: TextProps) {
  const context = useContext(ExpandableTextContext);
  const isClamped = z.boolean().catch(true).parse(context.isClamped);

  return (
    <Text
      {...props}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      elementType="p"
      className={cn(props.className, {
        'line-clamp-2': isClamped,
      })}
    />
  );
}

function NoteForm({
  initialValues,
  id,
  onSubmit: SubmitHandler,
}: {
  initialValues?: FormFields;
  id?: string;
  onSubmit: (data: FormFields) => PromiseLike<void>;
}) {
  const { t } = useLocale();
  const form = useForm<FormFields>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
  });
  const onSubmit = form.handleSubmit(SubmitHandler);

  return (
    <form id={id ?? 'create-note-form'} onSubmit={stopPropagate(onSubmit)}>
      <Controller
        control={form.control}
        name="note"
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            className="relative"
            isInvalid={!!fieldState.error?.message}
          >
            <Label className="sr-only">{t('Note')}</Label>
            <div className="relative">
              <TextArea
                rows={6}
                id="note"
                className={cn([
                  'form-input rounded-md py-2 resize-none text-gray-900 w-full placeholder:text-gray-400 sm:text-sm sm:leading-6',

                  // Background color
                  'bg-transparent dark:bg-white/5',

                  // Hide default focus styles
                  'focus:outline-none',

                  // Invalid state
                  'data-[invalid]:border-red-500 data-[invalid]:data-[hovered]:border-red-500',
                ])}
                //Add your note...
                placeholder={t('note_create_field_placeholder')}
              />
              {/* <div className="absolute bottom-0 right-0 px-3 pb-4">
                <CharacterCount maxLength={255} />
              </div> */}
            </div>
            <FieldErrorMessage control={form.control} field="note" />
          </TextField>
        )}
      />
    </form>
  );
}

function CharacterCount({ maxLength = 0 }: { maxLength?: number }) {
  const context = useSlottedContext(TextAreaContext);
  const count = context?.value?.toString().length ?? 0;
  return (
    <span
      className={cn([
        'text-sm',
        {
          'text-gray-500': count <= maxLength,
          'text-red-500': count > maxLength,
        },
      ])}
    >
      {count}/{maxLength}
    </span>
  );
}

function DeleteNoteAlertDialog({ note }: { note: ServiceLocationNote }) {
  const { t } = useLocale();
  const form = useForm<ServiceLocationNote>({ defaultValues: note });
  const snackbar = useSnackbar();
  const [deleteNote] =
    ServiceLocationService.endpoints.deleteNote.useMutation();
  const isLoading = form.formState.isSubmitting;

  return (
    <DialogTrigger>
      <Button
        type="button"
        className="text-xs font-semibold text-slate-600 hover:text-red-500"
      >
        {t('Delete')}
      </Button>
      <ModalOverlay className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/50 py-3 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0">
        <Modal className="relative z-50 grid w-full max-w-lg gap-4 rounded-2xl border bg-white p-6 shadow-lg duration-200 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 md:w-full">
          <Dialog className="outline-none">
            {(props) => (
              <>
                <CloseButton {...props} />

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertTriangle
                      className="h-5 w-5 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Heading
                      slot="title"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {t('Delete Note')}
                    </Heading>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {/* Are you sure you want to delete this note? This action
                        cannot be undone  */}
                        {t('note_delete_alert_description')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <form
                    action=""
                    onSubmit={stopPropagate(
                      form.handleSubmit(async (data) => {
                        const result = await deleteNote({ id: data.id });
                        if (isMutationSuccess(result)) {
                          props.close();
                          snackbar.enqueue({
                            // message: 'Note Deleted'
                            message: t('note_delete_success'),
                          });
                        } else {
                          snackbar.enqueue({
                            // message: 'Failed to delete note. Try again.',
                            message: t('note_delete_error'),
                          });
                        }
                      }),
                    )}
                  >
                    <Button
                      type="submit"
                      className={cn([
                        'inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto', // Disabled
                        // Disabled
                        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                      ])}
                      isDisabled={isLoading}
                    >
                      {isLoading && (
                        <Spinner className="mr-2" $size={16} $borderWidth={2} />
                      )}
                      {t('Delete Note')}
                    </Button>
                  </form>
                  <Button
                    type="button"
                    onPress={props.close}
                    className={cn([
                      'mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto',
                      // Disabled
                      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                    ])}
                  >
                    {t('Cancel')}
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

function EditNoteDialog({ note }: { note: ServiceLocationNote }) {
  const { t } = useLocale();
  const snackbar = useSnackbar();
  const [updateNote, mutation] =
    ServiceLocationService.endpoints.updateNote.useMutation();
  const isLoading = mutation.isLoading;

  return (
    <DialogTrigger>
      <Button
        type="button"
        className="text-xs font-semibold text-slate-600 hover:text-slate-400"
      >
        {t('Edit')}
      </Button>
      <ModalOverlay className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/50 py-3 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0">
        <Modal
          className="relative z-50 grid w-full max-w-lg gap-4 rounded-2xl border bg-white p-6 shadow-lg duration-200 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 md:w-full"
          isDismissable
        >
          <Dialog className="outline-none">
            {(props) => (
              <>
                <CloseButton {...props} />

                <Heading
                  slot="title"
                  className="text-lg font-semibold leading-none tracking-tight"
                >
                  {t('Edit Note')}
                </Heading>
                <div className="mt-4">
                  <NoteForm
                    id="update-note-form"
                    initialValues={{ note: note.noteValue }}
                    onSubmit={async (data) => {
                      const result = await updateNote({
                        noteId: note.id,
                        noteValue: data.note,
                      });
                      if (isMutationSuccess(result)) {
                        props.close();
                        snackbar.enqueue({
                          // message: 'Note Updated!'
                          message: t('note_update_success'),
                        });
                      } else {
                        snackbar.enqueue({
                          // message: 'Failed to add note. Try again.',
                          message: t('note_update_error'),
                        });
                      }
                    }}
                  />
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <Button
                      type="submit"
                      form="update-note-form"
                      className={cn([
                        'inline-flex w-full justify-center rounded-md bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary-500 sm:ml-3 sm:w-auto', // Disabled
                        // Disabled
                        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                      ])}
                      isDisabled={isLoading}
                    >
                      {isLoading && (
                        <Spinner className="mr-2" $size={16} $borderWidth={2} />
                      )}
                      {t('Save Note')}
                    </Button>
                    <Button
                      type="button"
                      onPress={props.close}
                      className={cn([
                        'mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto',
                        // Disabled
                        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                      ])}
                    >
                      {t('Cancel')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

function CloseButton(props: { close: VoidFunction }) {
  const { t } = useLocale();
  return (
    <Button
      onPress={props.close}
      className="focus:ring-ring absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[entering]:bg-accent data-[entering]:text-muted-foreground"
    >
      <X className="h-4 w-4" />
      <span className="sr-only">{t('Close')}</span>
    </Button>
  );
}

function FieldErrorMessage<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field,
  children,
}: {
  control: Control<T>;
  field: TName;
  children?: (message: string) => React.ReactNode;
}) {
  const { t, i18n } = useLocale();
  const { errors } = useFormState({ control, name: field, exact: true });
  const error = getFieldError(errors, field, '');

  if (!error) {
    return null;
  }
  const { message: messageFromRegister } = error;
  const errorMessage = messageFromRegister;
  const keyedMessage = errorMessage ? `${errorMessage}` : '';

  /**
   * The zod `message` may be passed a translation key (ex:errors.undefined) to handle custom
   * error message without `refine`.
   */
  const keyedMessageExist = keyedMessage
    ? i18n.exists(keyedMessage, {
        ns: 'zod',
      })
    : false;

  const translatedMessage = keyedMessageExist
    ? t(keyedMessage, { ns: 'zod' })
    : // This should already contain the translated message if no `keyed` message
    errorMessage
    ? errorMessage
    : t('default_validation_message', {
        ns: 'zod',
      });

  if (!children) {
    return (
      <Text
        data-slot="error"
        slot="errorMessage"
        className="block text-base/6 text-red-600 data-[disabled]:opacity-50 dark:text-red-500 sm:text-sm/6"
      >
        {translatedMessage}
      </Text>
    );
  }

  return <>{children(translatedMessage)}</>;
}
